import React from 'react'
import {
  Wind,
  Cloud,
  Droplets,
  Gauge,
  Sun,
  Flame,
  Anchor,
  Zap,
  Fuel,
  AlertTriangle,
  Cable,
  Plane,
  Atom,
} from 'lucide-react'

export interface OverlayConfig {
  label: string
  legendTitle: string
  legendUnit: string
  gradient: string
  overlayGradient: string
  min: string
  max: string
}

export const overlayConfigs: Record<string, OverlayConfig> = {
  'Air Temperature 2m': {
    label: 'Air Temperature 2m',
    legendTitle: 'Air Temperature @ 2m',
    legendUnit: '°C',
    gradient:
      'linear-gradient(to right, #1e40af, #3b82f6, #22d3ee, #fbbf24, #ef4444, #991b1b)',
    overlayGradient:
      'radial-gradient(ellipse at 40% 35%, rgba(239,68,68,0.35) 0%, rgba(251,191,36,0.25) 25%, rgba(59,130,246,0.3) 50%, rgba(30,64,175,0.2) 75%, transparent 100%)',
    min: '-20',
    max: '40',
  },
  'Air Pressure Sea Level': {
    label: 'Air Pressure Sea Level',
    legendTitle: 'Air Pressure @ Sea Level',
    legendUnit: 'hPa',
    gradient:
      'linear-gradient(to right, #7c3aed, #6366f1, #06b6d4, #10b981, #84cc16)',
    overlayGradient:
      'radial-gradient(ellipse at 55% 40%, rgba(99,102,241,0.35) 0%, rgba(6,182,212,0.25) 30%, rgba(16,185,129,0.2) 60%, transparent 85%)',
    min: '980',
    max: '1040',
  },
  'Geopotential Height 500hPa': {
    label: 'Geopotential Height 500hPa',
    legendTitle: 'Geopotential Height @ 500hPa',
    legendUnit: 'gpm',
    gradient:
      'linear-gradient(to right, #7c3aed, #3b82f6, #94a3b8, #f97316, #ef4444)',
    overlayGradient:
      'radial-gradient(ellipse at 45% 45%, rgba(16,185,129,0.35) 0%, rgba(59,130,246,0.3) 30%, rgba(236,72,153,0.2) 60%, transparent 80%)',
    min: '4800',
    max: '6000',
  },
  'Surface Solar Radiation': {
    label: 'Surface Solar Radiation',
    legendTitle: 'Surface Solar Radiation',
    legendUnit: 'W/m²',
    gradient:
      'linear-gradient(to right, #1e293b, #854d0e, #eab308, #fde047, #fefce8)',
    overlayGradient:
      'radial-gradient(ellipse at 50% 50%, rgba(234,179,8,0.4) 0%, rgba(251,191,36,0.25) 30%, rgba(133,77,14,0.15) 60%, transparent 85%)',
    min: '0',
    max: '800',
  },
}

export const MODEL_COLORS: Record<string, string> = {
  'EPT-2': '#EC4899',
  'EPT-2 Early': '#d946ef',
  'EPT-2 HRRR': '#f43f5e',
  'EC-IFS': '#06b6d4',
  GFS: '#f59e0b',
  'EPT-2e': '#8b5cf6',
}

export const LOCATION_COORDS: Record<
  string,
  { lat: number; lng: number; zoom: number }
> = {
  germany: { lat: 51.2, lng: 10.4, zoom: 6 },
  france: { lat: 46.6, lng: 2.2, zoom: 6 },
  uk: { lat: 54, lng: -2, zoom: 6 },
  'united kingdom': { lat: 54, lng: -2, zoom: 6 },
  britain: { lat: 54, lng: -2, zoom: 6 },
  england: { lat: 52.5, lng: -1.5, zoom: 7 },
  scotland: { lat: 56.8, lng: -4.2, zoom: 7 },
  wales: { lat: 52.1, lng: -3.6, zoom: 8 },
  ireland: { lat: 53.4, lng: -8.2, zoom: 7 },
  'northern ireland': { lat: 54.6, lng: -6.7, zoom: 8 },
  spain: { lat: 40.4, lng: -3.7, zoom: 6 },
  italy: { lat: 42.5, lng: 12.5, zoom: 6 },
  portugal: { lat: 39.4, lng: -8.2, zoom: 7 },
  greece: { lat: 39.1, lng: 22.0, zoom: 6 },
  turkey: { lat: 39.9, lng: 32.9, zoom: 6 },
  ukraine: { lat: 48.4, lng: 31.2, zoom: 6 },
  romania: { lat: 45.9, lng: 24.9, zoom: 6 },
  hungary: { lat: 47.2, lng: 19.5, zoom: 7 },
  austria: { lat: 47.5, lng: 14.6, zoom: 7 },
  belgium: { lat: 50.8, lng: 4.4, zoom: 8 },
  'czech republic': { lat: 49.8, lng: 15.5, zoom: 7 },
  czechia: { lat: 49.8, lng: 15.5, zoom: 7 },
  croatia: { lat: 45.1, lng: 15.2, zoom: 7 },
  serbia: { lat: 44.0, lng: 21.0, zoom: 7 },
  bulgaria: { lat: 42.7, lng: 25.5, zoom: 7 },
  finland: { lat: 64.0, lng: 26.0, zoom: 5 },
  iceland: { lat: 64.9, lng: -18.5, zoom: 6 },
  estonia: { lat: 58.6, lng: 25.0, zoom: 7 },
  latvia: { lat: 56.9, lng: 24.1, zoom: 7 },
  lithuania: { lat: 55.2, lng: 23.9, zoom: 7 },
  slovakia: { lat: 48.7, lng: 19.7, zoom: 7 },
  slovenia: { lat: 46.1, lng: 14.8, zoom: 8 },
  bosnia: { lat: 43.9, lng: 17.7, zoom: 7 },
  albania: { lat: 41.3, lng: 20.0, zoom: 8 },
  'north macedonia': { lat: 41.5, lng: 21.7, zoom: 8 },
  montenegro: { lat: 42.7, lng: 19.4, zoom: 8 },
  moldova: { lat: 47.0, lng: 28.9, zoom: 7 },
  belarus: { lat: 53.7, lng: 27.9, zoom: 6 },
  russia: { lat: 55.8, lng: 37.6, zoom: 5 },
  morocco: { lat: 31.8, lng: -7.1, zoom: 6 },
  scandinavia: { lat: 62, lng: 15, zoom: 5 },
  norway: { lat: 62, lng: 10, zoom: 5 },
  sweden: { lat: 62, lng: 15, zoom: 5 },
  denmark: { lat: 56, lng: 10, zoom: 7 },
  poland: { lat: 52, lng: 19.5, zoom: 6 },
  netherlands: { lat: 52.3, lng: 5.3, zoom: 7 },
  holland: { lat: 52.3, lng: 5.3, zoom: 7 },
  switzerland: { lat: 46.8, lng: 8.2, zoom: 8 },
  'north sea': { lat: 56, lng: 3, zoom: 5 },
  'baltic sea': { lat: 58, lng: 20, zoom: 5 },
  baltic: { lat: 58, lng: 20, zoom: 5 },
  mediterranean: { lat: 38, lng: 15, zoom: 5 },
  balkans: { lat: 42.5, lng: 21, zoom: 6 },
  nordic: { lat: 62, lng: 15, zoom: 4 },
  'iberian peninsula': { lat: 39.5, lng: -3, zoom: 5 },
  iberia: { lat: 39.5, lng: -3, zoom: 5 },
  london: { lat: 51.5, lng: -0.12, zoom: 10 },
  paris: { lat: 48.86, lng: 2.35, zoom: 10 },
  berlin: { lat: 52.52, lng: 13.4, zoom: 10 },
  madrid: { lat: 40.42, lng: -3.7, zoom: 10 },
  rome: { lat: 41.9, lng: 12.5, zoom: 10 },
  dublin: { lat: 53.35, lng: -6.26, zoom: 10 },
  amsterdam: { lat: 52.37, lng: 4.9, zoom: 10 },
  brussels: { lat: 50.85, lng: 4.35, zoom: 10 },
  vienna: { lat: 48.21, lng: 16.37, zoom: 10 },
  warsaw: { lat: 52.23, lng: 21.0, zoom: 10 },
  prague: { lat: 50.08, lng: 14.44, zoom: 10 },
  budapest: { lat: 47.5, lng: 19.04, zoom: 10 },
  bucharest: { lat: 44.43, lng: 26.1, zoom: 10 },
  athens: { lat: 37.98, lng: 23.73, zoom: 10 },
  istanbul: { lat: 41.01, lng: 28.98, zoom: 10 },
  kyiv: { lat: 50.45, lng: 30.52, zoom: 10 },
  kiev: { lat: 50.45, lng: 30.52, zoom: 10 },
  lisbon: { lat: 38.72, lng: -9.14, zoom: 10 },
  stockholm: { lat: 59.33, lng: 18.07, zoom: 10 },
  oslo: { lat: 59.91, lng: 10.75, zoom: 10 },
  helsinki: { lat: 60.17, lng: 24.94, zoom: 10 },
  copenhagen: { lat: 55.68, lng: 12.57, zoom: 10 },
  munich: { lat: 48.14, lng: 11.58, zoom: 10 },
  hamburg: { lat: 53.55, lng: 9.99, zoom: 10 },
  zurich: { lat: 47.37, lng: 8.54, zoom: 10 },
  europe: { lat: 48, lng: 10, zoom: 4 },
}

export const VARIABLE_ALIASES: Record<string, string> = {
  temperature: 'Air Temperature 2m',
  temp: 'Air Temperature 2m',
  t2m: 'Air Temperature 2m',
  'air temp': 'Air Temperature 2m',
  pressure: 'Air Pressure Sea Level',
  'air pressure': 'Air Pressure Sea Level',
  'sea level pressure': 'Air Pressure Sea Level',
  'msl pressure': 'Air Pressure Sea Level',
  geopotential: 'Geopotential Height 500hPa',
  '500hpa': 'Geopotential Height 500hPa',
  wind: 'Geopotential Height 500hPa',
  'wind speed': 'Geopotential Height 500hPa',
  solar: 'Surface Solar Radiation',
  'solar radiation': 'Surface Solar Radiation',
  radiation: 'Surface Solar Radiation',
  sunshine: 'Surface Solar Radiation',
  irradiance: 'Surface Solar Radiation',
}

export const POI_KEYWORD_MAP: Record<string, string[]> = {
  'solar farm': ['solar_farm'],
  'solar farms': ['solar_farm'],
  'solar park': ['solar_farm'],
  solar: ['solar_farm'],
  'wind farm': ['wind_farm'],
  'wind farms': ['wind_farm'],
  'wind turbine': ['wind_farm'],
  'offshore wind': ['wind_farm'],
  'onshore wind': ['wind_farm'],
  wind: ['wind_farm'],
  'oil and gas': ['oil_gas'],
  'oil platform': ['oil_gas'],
  'gas platform': ['oil_gas'],
  nuclear: ['nuclear'],
  'nuclear plant': ['nuclear'],
  hydro: ['hydro'],
  hydroelectric: ['hydro'],
  lng: ['lng'],
  'lng terminal': ['lng'],
  port: ['port'],
  ports: ['port'],
  airport: ['airport'],
  airports: ['airport'],
  interconnector: ['interconnector'],
  'grid interconnector': ['interconnector'],
  conflict: ['conflict'],
  'conflict zone': ['conflict'],
  oil: ['oil_gas'],
  gas: ['oil_gas', 'lng'],
  renewable: ['wind_farm', 'solar_farm', 'hydro'],
  renewables: ['wind_farm', 'solar_farm', 'hydro'],
}

export const LAYER_KEYWORD_MAP: Record<string, string[]> = {
  'cloud cover': ['clouds'],
  clouds: ['clouds'],
  precipitation: ['precipitation'],
  rain: ['precipitation'],
  'wind speed': ['wind'],
  wind: ['wind'],
  pressure: ['pressure'],
  temperature: ['temperature'],
  temp: ['temperature'],
}

export interface WeatherLayer {
  id: string
  label: string
  owmLayer: string
  icon: React.ReactNode
}

export const WEATHER_LAYERS: WeatherLayer[] = [
  {
    id: 'temperature',
    label: 'Temperature',
    owmLayer: 'temp_new',
    icon: React.createElement(Sun, { size: 16, className: 'text-amber-400' }),
  },
  {
    id: 'pressure',
    label: 'Air Pressure',
    owmLayer: 'pressure_new',
    icon: React.createElement(Gauge, { size: 16, className: 'text-slate-400' }),
  },
  {
    id: 'clouds',
    label: 'Cloud Cover',
    owmLayer: 'clouds_new',
    icon: React.createElement(Cloud, { size: 16, className: 'text-slate-400' }),
  },
  {
    id: 'precipitation',
    label: 'Precipitation',
    owmLayer: 'precipitation_new',
    icon: React.createElement(Droplets, {
      size: 16,
      className: 'text-slate-400',
    }),
  },
  {
    id: 'wind',
    label: 'Wind Speed',
    owmLayer: 'wind_new',
    icon: React.createElement(Wind, { size: 16, className: 'text-slate-400' }),
  },
]

export interface POICategory {
  id: string
  label: string
  color: string
  icon: React.ReactNode
  radius: number
}

export const POI_CATEGORIES: POICategory[] = [
  {
    id: 'wind_farm',
    label: 'Wind Farms',
    color: '#3b82f6',
    icon: React.createElement(Wind, { size: 16 }),
    radius: 5,
  },
  {
    id: 'solar_farm',
    label: 'Solar Farms (>50 MW)',
    color: '#eab308',
    icon: React.createElement(Sun, { size: 16 }),
    radius: 5,
  },
  {
    id: 'oil_gas',
    label: 'Oil & Gas Platforms',
    color: '#f97316',
    icon: React.createElement(Flame, { size: 16 }),
    radius: 4,
  },
  {
    id: 'port',
    label: 'Major Ports',
    color: '#06b6d4',
    icon: React.createElement(Anchor, { size: 16 }),
    radius: 5,
  },
  {
    id: 'airport',
    label: 'Airports',
    color: '#a78bfa',
    icon: React.createElement(Plane, { size: 16 }),
    radius: 4,
  },
  {
    id: 'nuclear',
    label: 'Nuclear Plants',
    color: '#10b981',
    icon: React.createElement(Atom, { size: 16 }),
    radius: 6,
  },
  {
    id: 'hydro',
    label: 'Hydroelectric Dams',
    color: '#38bdf8',
    icon: React.createElement(Zap, { size: 16 }),
    radius: 5,
  },
  {
    id: 'lng',
    label: 'LNG Terminals',
    color: '#f472b6',
    icon: React.createElement(Fuel, { size: 16 }),
    radius: 5,
  },
  {
    id: 'interconnector',
    label: 'Grid Interconnectors',
    color: '#c084fc',
    icon: React.createElement(Cable, { size: 16 }),
    radius: 4,
  },
  {
    id: 'conflict',
    label: 'Conflict Zones',
    color: '#ef4444',
    icon: React.createElement(AlertTriangle, { size: 16 }),
    radius: 8,
  },
]

export interface POIPoint {
  categoryId: string
  name: string
  lat: number
  lng: number
  detail?: string
}

export const POI_DATA: POIPoint[] = [
  // ── Wind Farms ──────────────────────────────────────────────────────────
  {
    categoryId: 'wind_farm',
    name: 'Hornsea One',
    lat: 53.88,
    lng: 1.79,
    detail: '1.2 GW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Hornsea Two',
    lat: 53.95,
    lng: 1.95,
    detail: '1.4 GW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Dogger Bank A/B',
    lat: 54.75,
    lng: 2.0,
    detail: '2.4 GW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Borssele 1&2',
    lat: 51.7,
    lng: 3.0,
    detail: '752 MW · Offshore NL',
  },
  {
    categoryId: 'wind_farm',
    name: 'Gemini Wind Park',
    lat: 54.05,
    lng: 5.97,
    detail: '600 MW · Offshore NL',
  },
  {
    categoryId: 'wind_farm',
    name: 'Hollandse Kust Noord',
    lat: 52.85,
    lng: 4.55,
    detail: '759 MW · Offshore NL',
  },
  {
    categoryId: 'wind_farm',
    name: 'Anholt Offshore',
    lat: 56.6,
    lng: 11.22,
    detail: '400 MW · Offshore DK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Horns Rev 3',
    lat: 55.85,
    lng: 7.78,
    detail: '407 MW · Offshore DK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Kriegers Flak',
    lat: 55.08,
    lng: 13.0,
    detail: '605 MW · Offshore DK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Sandbank',
    lat: 55.4,
    lng: 7.2,
    detail: '288 MW · Offshore DE',
  },
  {
    categoryId: 'wind_farm',
    name: 'DanTysk',
    lat: 55.3,
    lng: 7.0,
    detail: '288 MW · Offshore DE',
  },
  {
    categoryId: 'wind_farm',
    name: 'Gode Wind 1&2',
    lat: 54.9,
    lng: 7.0,
    detail: '582 MW · Offshore DE',
  },
  {
    categoryId: 'wind_farm',
    name: 'Wikinger',
    lat: 54.83,
    lng: 14.07,
    detail: '350 MW · Offshore DE',
  },
  {
    categoryId: 'wind_farm',
    name: 'Sheringham Shoal',
    lat: 53.07,
    lng: 1.15,
    detail: '317 MW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Walney Extension',
    lat: 54.05,
    lng: -3.6,
    detail: '659 MW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Beatrice Offshore',
    lat: 58.1,
    lng: -2.9,
    detail: '588 MW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Moray East',
    lat: 57.8,
    lng: -2.5,
    detail: '950 MW · Offshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Vineyard Wind (Hywind)',
    lat: 59.0,
    lng: 4.5,
    detail: '30 MW · Floating NO',
  },
  {
    categoryId: 'wind_farm',
    name: 'Fosen Vind',
    lat: 63.8,
    lng: 9.5,
    detail: '1.1 GW · Onshore NO',
  },
  {
    categoryId: 'wind_farm',
    name: 'Markbygden',
    lat: 65.5,
    lng: 21.0,
    detail: '1.1 GW · Onshore SE',
  },
  {
    categoryId: 'wind_farm',
    name: 'Middelgrunden',
    lat: 55.68,
    lng: 12.67,
    detail: '40 MW · Offshore DK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Whitelee Wind Farm',
    lat: 55.7,
    lng: -4.3,
    detail: '539 MW · Onshore UK',
  },
  {
    categoryId: 'wind_farm',
    name: 'Fantanele-Cogealac',
    lat: 44.35,
    lng: 28.6,
    detail: '600 MW · Onshore RO',
  },
  {
    categoryId: 'wind_farm',
    name: 'Peñascal (Iberdrola)',
    lat: 40.5,
    lng: -4.2,
    detail: '255 MW · Onshore ES',
  },
  // ── Solar Farms ─────────────────────────────────────────────────────────
  {
    categoryId: 'solar_farm',
    name: 'Núñez de Balboa',
    lat: 38.6,
    lng: -6.8,
    detail: '500 MW · Spain',
  },
  {
    categoryId: 'solar_farm',
    name: 'Francisco Pizarro',
    lat: 39.2,
    lng: -6.1,
    detail: '590 MW · Spain',
  },
  {
    categoryId: 'solar_farm',
    name: 'Cestas Solar Park',
    lat: 44.73,
    lng: -0.78,
    detail: '300 MW · France',
  },
  {
    categoryId: 'solar_farm',
    name: 'Toul-Rosières',
    lat: 48.68,
    lng: 5.88,
    detail: '115 MW · France',
  },
  {
    categoryId: 'solar_farm',
    name: 'Senftenberg Solar',
    lat: 51.52,
    lng: 14.0,
    detail: '78 MW · Germany',
  },
  {
    categoryId: 'solar_farm',
    name: 'Lieberose Solar',
    lat: 51.97,
    lng: 14.3,
    detail: '71 MW · Germany',
  },
  {
    categoryId: 'solar_farm',
    name: 'Montalto di Castro',
    lat: 42.35,
    lng: 11.6,
    detail: '84 MW · Italy',
  },
  {
    categoryId: 'solar_farm',
    name: 'Serre Solar',
    lat: 40.6,
    lng: 15.2,
    detail: '72 MW · Italy',
  },
  {
    categoryId: 'solar_farm',
    name: 'Ouarzazate (NOOR)',
    lat: 30.9,
    lng: -6.9,
    detail: '580 MW · Morocco',
  },
  {
    categoryId: 'solar_farm',
    name: 'Benban Solar Park',
    lat: 24.4,
    lng: 32.7,
    detail: '1.65 GW · Egypt',
  },
  {
    categoryId: 'solar_farm',
    name: 'Charanka Solar',
    lat: 23.9,
    lng: 71.2,
    detail: '224 MW · India',
  },
  {
    categoryId: 'solar_farm',
    name: 'Kalkbult Solar',
    lat: -30.1,
    lng: 23.5,
    detail: '75 MW · South Africa',
  },
  // ── Oil & Gas ────────────────────────────────────────────────────────────
  {
    categoryId: 'oil_gas',
    name: 'Ekofisk Complex',
    lat: 56.55,
    lng: 3.21,
    detail: 'Oil/Gas · North Sea NO',
  },
  {
    categoryId: 'oil_gas',
    name: 'Johan Sverdrup',
    lat: 58.97,
    lng: 2.53,
    detail: '755 kbbl/d · North Sea NO',
  },
  {
    categoryId: 'oil_gas',
    name: 'Troll Field',
    lat: 60.65,
    lng: 3.72,
    detail: 'Gas · North Sea NO',
  },
  {
    categoryId: 'oil_gas',
    name: 'Statfjord',
    lat: 61.25,
    lng: 1.85,
    detail: 'Oil · North Sea NO',
  },
  {
    categoryId: 'oil_gas',
    name: 'Oseberg',
    lat: 60.5,
    lng: 2.8,
    detail: 'Oil/Gas · North Sea NO',
  },
  {
    categoryId: 'oil_gas',
    name: 'Brent Field',
    lat: 61.05,
    lng: 1.72,
    detail: 'Oil · North Sea UK',
  },
  {
    categoryId: 'oil_gas',
    name: 'Forties Pipeline',
    lat: 57.75,
    lng: 0.5,
    detail: 'Oil · North Sea UK',
  },
  {
    categoryId: 'oil_gas',
    name: 'Groningen Gas Field',
    lat: 53.3,
    lng: 6.8,
    detail: 'Gas · Netherlands',
  },
  {
    categoryId: 'oil_gas',
    name: 'Shtokman (proposed)',
    lat: 72.5,
    lng: 43.5,
    detail: 'Gas · Barents Sea RU',
  },
  {
    categoryId: 'oil_gas',
    name: 'Shah Deniz',
    lat: 39.5,
    lng: 50.5,
    detail: 'Gas · Caspian AZ',
  },
  {
    categoryId: 'oil_gas',
    name: 'Kashagan',
    lat: 45.5,
    lng: 52.8,
    detail: 'Oil · Caspian KZ',
  },
  // ── Major Ports ──────────────────────────────────────────────────────────
  {
    categoryId: 'port',
    name: 'Rotterdam',
    lat: 51.9,
    lng: 4.5,
    detail: "Europe's largest port · NL",
  },
  {
    categoryId: 'port',
    name: 'Antwerp',
    lat: 51.25,
    lng: 4.4,
    detail: '2nd largest EU port · BE',
  },
  {
    categoryId: 'port',
    name: 'Hamburg',
    lat: 53.55,
    lng: 9.97,
    detail: 'Germany · Major LNG hub',
  },
  {
    categoryId: 'port',
    name: 'Bremerhaven',
    lat: 53.55,
    lng: 8.58,
    detail: 'Germany · Container',
  },
  {
    categoryId: 'port',
    name: 'Felixstowe',
    lat: 51.96,
    lng: 1.35,
    detail: 'UK · Largest container',
  },
  {
    categoryId: 'port',
    name: 'Le Havre',
    lat: 49.49,
    lng: 0.11,
    detail: 'France · Atlantic gateway',
  },
  {
    categoryId: 'port',
    name: 'Marseille',
    lat: 43.3,
    lng: 5.35,
    detail: 'France · Mediterranean',
  },
  {
    categoryId: 'port',
    name: 'Genoa',
    lat: 44.41,
    lng: 8.93,
    detail: 'Italy · Mediterranean',
  },
  {
    categoryId: 'port',
    name: 'Piraeus',
    lat: 37.94,
    lng: 23.63,
    detail: 'Greece · SE Europe hub',
  },
  {
    categoryId: 'port',
    name: 'Istanbul (Haydarpaşa)',
    lat: 40.99,
    lng: 29.02,
    detail: 'Turkey · Bosphorus',
  },
  {
    categoryId: 'port',
    name: 'Gdańsk',
    lat: 54.37,
    lng: 18.66,
    detail: 'Poland · Baltic',
  },
  {
    categoryId: 'port',
    name: 'Constanța',
    lat: 44.18,
    lng: 28.65,
    detail: 'Romania · Black Sea',
  },
  {
    categoryId: 'port',
    name: 'Algeciras',
    lat: 36.13,
    lng: -5.45,
    detail: 'Spain · Strait of Gibraltar',
  },
  {
    categoryId: 'port',
    name: 'Valencia',
    lat: 39.45,
    lng: -0.32,
    detail: 'Spain · Mediterranean',
  },
  // ── Airports ─────────────────────────────────────────────────────────────
  {
    categoryId: 'airport',
    name: 'London Heathrow',
    lat: 51.47,
    lng: -0.46,
    detail: 'LHR · UK',
  },
  {
    categoryId: 'airport',
    name: 'London Gatwick',
    lat: 51.15,
    lng: -0.18,
    detail: 'LGW · UK',
  },
  {
    categoryId: 'airport',
    name: 'Frankfurt',
    lat: 50.03,
    lng: 8.57,
    detail: 'FRA · Germany',
  },
  {
    categoryId: 'airport',
    name: 'Munich',
    lat: 48.35,
    lng: 11.79,
    detail: 'MUC · Germany',
  },
  {
    categoryId: 'airport',
    name: 'Paris CDG',
    lat: 49.01,
    lng: 2.55,
    detail: 'CDG · France',
  },
  {
    categoryId: 'airport',
    name: 'Paris Orly',
    lat: 48.72,
    lng: 2.38,
    detail: 'ORY · France',
  },
  {
    categoryId: 'airport',
    name: 'Amsterdam Schiphol',
    lat: 52.31,
    lng: 4.77,
    detail: 'AMS · Netherlands',
  },
  {
    categoryId: 'airport',
    name: 'Madrid Barajas',
    lat: 40.47,
    lng: -3.56,
    detail: 'MAD · Spain',
  },
  {
    categoryId: 'airport',
    name: 'Barcelona El Prat',
    lat: 41.3,
    lng: 2.08,
    detail: 'BCN · Spain',
  },
  {
    categoryId: 'airport',
    name: 'Rome Fiumicino',
    lat: 41.8,
    lng: 12.25,
    detail: 'FCO · Italy',
  },
  {
    categoryId: 'airport',
    name: 'Milan Malpensa',
    lat: 45.63,
    lng: 8.72,
    detail: 'MXP · Italy',
  },
  {
    categoryId: 'airport',
    name: 'Brussels',
    lat: 50.9,
    lng: 4.48,
    detail: 'BRU · Belgium',
  },
  {
    categoryId: 'airport',
    name: 'Zurich',
    lat: 47.46,
    lng: 8.55,
    detail: 'ZRH · Switzerland',
  },
  {
    categoryId: 'airport',
    name: 'Vienna',
    lat: 48.11,
    lng: 16.57,
    detail: 'VIE · Austria',
  },
  {
    categoryId: 'airport',
    name: 'Warsaw Chopin',
    lat: 52.17,
    lng: 20.97,
    detail: 'WAW · Poland',
  },
  {
    categoryId: 'airport',
    name: 'Copenhagen',
    lat: 55.62,
    lng: 12.66,
    detail: 'CPH · Denmark',
  },
  {
    categoryId: 'airport',
    name: 'Stockholm Arlanda',
    lat: 59.65,
    lng: 17.93,
    detail: 'ARN · Sweden',
  },
  {
    categoryId: 'airport',
    name: 'Oslo Gardermoen',
    lat: 60.2,
    lng: 11.08,
    detail: 'OSL · Norway',
  },
  {
    categoryId: 'airport',
    name: 'Helsinki Vantaa',
    lat: 60.32,
    lng: 24.96,
    detail: 'HEL · Finland',
  },
  {
    categoryId: 'airport',
    name: 'Istanbul Atatürk',
    lat: 40.98,
    lng: 28.82,
    detail: 'IST · Turkey',
  },
  {
    categoryId: 'airport',
    name: 'Athens',
    lat: 37.94,
    lng: 23.94,
    detail: 'ATH · Greece',
  },
  // ── Nuclear Plants ───────────────────────────────────────────────────────
  {
    categoryId: 'nuclear',
    name: 'Hinkley Point C',
    lat: 51.21,
    lng: -3.13,
    detail: '3.2 GW · UK (under construction)',
  },
  {
    categoryId: 'nuclear',
    name: 'Sizewell B',
    lat: 52.21,
    lng: 1.62,
    detail: '1.2 GW · UK',
  },
  {
    categoryId: 'nuclear',
    name: 'Gravelines',
    lat: 51.01,
    lng: 2.11,
    detail: '5.5 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Paluel',
    lat: 49.86,
    lng: 0.63,
    detail: '5.3 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Cattenom',
    lat: 49.4,
    lng: 6.22,
    detail: '5.3 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Bugey',
    lat: 45.8,
    lng: 5.27,
    detail: '3.6 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Tricastin',
    lat: 44.33,
    lng: 4.73,
    detail: '3.6 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Flamanville',
    lat: 49.53,
    lng: -1.88,
    detail: '2.6 GW · France',
  },
  {
    categoryId: 'nuclear',
    name: 'Leibstadt',
    lat: 47.6,
    lng: 8.18,
    detail: '1.2 GW · Switzerland',
  },
  {
    categoryId: 'nuclear',
    name: 'Beznau',
    lat: 47.52,
    lng: 8.11,
    detail: '730 MW · Switzerland',
  },
  {
    categoryId: 'nuclear',
    name: 'Isar 2',
    lat: 48.6,
    lng: 12.29,
    detail: '1.4 GW · Germany (closed 2023)',
  },
  {
    categoryId: 'nuclear',
    name: 'Doel',
    lat: 51.32,
    lng: 4.26,
    detail: '2.9 GW · Belgium',
  },
  {
    categoryId: 'nuclear',
    name: 'Tihange',
    lat: 50.53,
    lng: 5.27,
    detail: '3.0 GW · Belgium',
  },
  {
    categoryId: 'nuclear',
    name: 'Paks',
    lat: 46.57,
    lng: 18.85,
    detail: '2.0 GW · Hungary',
  },
  {
    categoryId: 'nuclear',
    name: 'Dukovany',
    lat: 49.09,
    lng: 16.15,
    detail: '2.0 GW · Czech Republic',
  },
  {
    categoryId: 'nuclear',
    name: 'Temelín',
    lat: 49.18,
    lng: 14.37,
    detail: '2.2 GW · Czech Republic',
  },
  {
    categoryId: 'nuclear',
    name: 'Zaporizhzhia',
    lat: 47.51,
    lng: 34.58,
    detail: '6.0 GW · Ukraine (occupied)',
  },
  {
    categoryId: 'nuclear',
    name: 'Forsmark',
    lat: 60.41,
    lng: 18.17,
    detail: '3.1 GW · Sweden',
  },
  {
    categoryId: 'nuclear',
    name: 'Ringhals',
    lat: 57.26,
    lng: 12.11,
    detail: '2.0 GW · Sweden',
  },
  {
    categoryId: 'nuclear',
    name: 'Olkiluoto 3',
    lat: 61.24,
    lng: 21.44,
    detail: '1.6 GW · Finland',
  },
  // ── Hydroelectric ────────────────────────────────────────────────────────
  {
    categoryId: 'hydro',
    name: 'Grande Dixence',
    lat: 46.08,
    lng: 7.4,
    detail: '2.0 GW · Switzerland',
  },
  {
    categoryId: 'hydro',
    name: 'Bieudron (Nendaz)',
    lat: 46.17,
    lng: 7.3,
    detail: '1.3 GW · Switzerland',
  },
  {
    categoryId: 'hydro',
    name: 'Kraftwerk Linth-Limmern',
    lat: 46.93,
    lng: 9.0,
    detail: '1.5 GW · Switzerland',
  },
  {
    categoryId: 'hydro',
    name: 'Kielder Reservoir',
    lat: 55.18,
    lng: -2.58,
    detail: 'Pumped storage · UK',
  },
  {
    categoryId: 'hydro',
    name: 'Dinorwig',
    lat: 53.12,
    lng: -4.12,
    detail: '1.7 GW · Wales UK',
  },
  {
    categoryId: 'hydro',
    name: 'Cruachan',
    lat: 56.4,
    lng: -5.1,
    detail: '440 MW · Scotland UK',
  },
  {
    categoryId: 'hydro',
    name: 'Porjus',
    lat: 66.96,
    lng: 19.83,
    detail: '440 MW · Sweden',
  },
  {
    categoryId: 'hydro',
    name: 'Harsprånget',
    lat: 66.1,
    lng: 20.8,
    detail: '977 MW · Sweden',
  },
  {
    categoryId: 'hydro',
    name: 'Tonstad',
    lat: 58.67,
    lng: 6.72,
    detail: '960 MW · Norway',
  },
  {
    categoryId: 'hydro',
    name: 'Kvilldal',
    lat: 59.5,
    lng: 6.5,
    detail: '1.2 GW · Norway',
  },
  {
    categoryId: 'hydro',
    name: 'Cahora Bassa',
    lat: -15.6,
    lng: 32.7,
    detail: '2.1 GW · Mozambique',
  },
  {
    categoryId: 'hydro',
    name: 'Gabčíkovo',
    lat: 47.88,
    lng: 17.59,
    detail: '720 MW · Slovakia/Hungary',
  },
  {
    categoryId: 'hydro',
    name: 'Iron Gate I',
    lat: 44.68,
    lng: 22.52,
    detail: '2.1 GW · Romania/Serbia',
  },
  // ── LNG Terminals ────────────────────────────────────────────────────────
  {
    categoryId: 'lng',
    name: 'Zeebrugge LNG',
    lat: 51.35,
    lng: 3.2,
    detail: 'Belgium · Import terminal',
  },
  {
    categoryId: 'lng',
    name: 'Gate Terminal Rotterdam',
    lat: 51.95,
    lng: 4.05,
    detail: 'Netherlands · 12 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Dunkerque LNG',
    lat: 51.05,
    lng: 2.35,
    detail: 'France · 13 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Montoir-de-Bretagne',
    lat: 47.3,
    lng: -2.15,
    detail: 'France · 10 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Fos Cavaou',
    lat: 43.38,
    lng: 4.88,
    detail: 'France · 8.25 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Isle of Grain',
    lat: 51.45,
    lng: 0.72,
    detail: 'UK · 14.8 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'South Hook LNG',
    lat: 51.7,
    lng: -5.05,
    detail: 'Wales UK · 21 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Dragon LNG',
    lat: 51.71,
    lng: -4.98,
    detail: 'Wales UK · 6 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Eemshaven LNG',
    lat: 53.44,
    lng: 6.83,
    detail: 'Netherlands · FSRU',
  },
  {
    categoryId: 'lng',
    name: 'Brunsbüttel LNG',
    lat: 53.9,
    lng: 9.15,
    detail: 'Germany · FSRU 2023',
  },
  {
    categoryId: 'lng',
    name: 'Deutsche ReGas Lubmin',
    lat: 54.14,
    lng: 13.66,
    detail: 'Germany · FSRU 2022',
  },
  {
    categoryId: 'lng',
    name: 'Sagunto LNG',
    lat: 39.67,
    lng: -0.22,
    detail: 'Spain · 8.8 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Barcelona LNG',
    lat: 41.35,
    lng: 2.18,
    detail: 'Spain · 17.5 bcm/y',
  },
  {
    categoryId: 'lng',
    name: 'Revithoussa LNG',
    lat: 37.93,
    lng: 23.38,
    detail: 'Greece · 7 bcm/y',
  },
  // ── Grid Interconnectors ─────────────────────────────────────────────────
  {
    categoryId: 'interconnector',
    name: 'NordLink',
    lat: 57.5,
    lng: 5.5,
    detail: 'NO↔DE · 1.4 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'North Sea Link',
    lat: 57.0,
    lng: -1.0,
    detail: 'NO↔UK · 1.4 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'NorNed',
    lat: 57.8,
    lng: 4.5,
    detail: 'NO↔NL · 700 MW',
  },
  {
    categoryId: 'interconnector',
    name: 'BritNed',
    lat: 51.9,
    lng: 3.8,
    detail: 'UK↔NL · 1.0 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'IFA (France–UK)',
    lat: 50.9,
    lng: 1.5,
    detail: 'FR↔UK · 2.0 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'IFA2',
    lat: 50.8,
    lng: 1.3,
    detail: 'FR↔UK · 1.0 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'ElecLink (Eurotunnel)',
    lat: 51.1,
    lng: 1.4,
    detail: 'FR↔UK · 1.0 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'Viking Link',
    lat: 55.5,
    lng: 8.0,
    detail: 'DK↔UK · 1.4 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'Skagerrak',
    lat: 57.7,
    lng: 9.5,
    detail: 'NO↔DK · 1.7 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'Kontek',
    lat: 54.5,
    lng: 12.5,
    detail: 'DE↔DK · 600 MW',
  },
  {
    categoryId: 'interconnector',
    name: 'Baltic Cable',
    lat: 55.5,
    lng: 13.5,
    detail: 'SE↔DE · 600 MW',
  },
  {
    categoryId: 'interconnector',
    name: 'SwePol Link',
    lat: 55.0,
    lng: 16.0,
    detail: 'SE↔PL · 600 MW',
  },
  {
    categoryId: 'interconnector',
    name: 'Estlink 1&2',
    lat: 59.5,
    lng: 24.5,
    detail: 'FI↔EE · 1.0 GW',
  },
  {
    categoryId: 'interconnector',
    name: 'LitPol Link',
    lat: 54.2,
    lng: 22.8,
    detail: 'LT↔PL · 500 MW',
  },
  {
    categoryId: 'interconnector',
    name: 'SAPEI (Sardinia–Italy)',
    lat: 40.5,
    lng: 9.5,
    detail: 'IT islands · 1.0 GW',
  },
  // ── Conflict Zones ───────────────────────────────────────────────────────
  {
    categoryId: 'conflict',
    name: 'Eastern Ukraine (Donbas)',
    lat: 48.5,
    lng: 37.5,
    detail: 'Active conflict zone',
  },
  {
    categoryId: 'conflict',
    name: 'Zaporizhzhia Nuclear Zone',
    lat: 47.51,
    lng: 34.58,
    detail: 'Nuclear plant under military control',
  },
  {
    categoryId: 'conflict',
    name: 'Kherson Oblast',
    lat: 46.6,
    lng: 32.6,
    detail: 'Active conflict zone',
  },
  {
    categoryId: 'conflict',
    name: 'Kharkiv Oblast',
    lat: 49.9,
    lng: 36.3,
    detail: 'Active conflict zone',
  },
  {
    categoryId: 'conflict',
    name: 'Gaza Strip',
    lat: 31.4,
    lng: 34.35,
    detail: 'Active conflict zone',
  },
  {
    categoryId: 'conflict',
    name: 'Syria (NE)',
    lat: 36.2,
    lng: 38.0,
    detail: 'Ongoing instability',
  },
  {
    categoryId: 'conflict',
    name: 'Libya (Tripolitania)',
    lat: 32.9,
    lng: 13.2,
    detail: 'Oil infrastructure risk',
  },
]

export const POI_ICON_SVGS: Record<string, string> = {
  wind_farm:
    '<path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/><path d="M9.6 4.6A2 2 0 1 1 11 8H2"/><path d="M12.6 19.4A2 2 0 1 0 14 16H2"/>',
  solar_farm:
    '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>',
  oil_gas:
    '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  port: '<circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="21"/><path d="M5 12l7 9 7-9"/>',
  airport:
    '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
  nuclear:
    '<circle cx="12" cy="12" r="1" fill="currentColor"/><path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z"/><path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z"/>',
  hydro:
    '<path d="M4 14l1-1 4 4 4-4 4 4 4-4 1 1"/><path d="M13 4l-1 4h4l-3 8"/>',
  lng: '<line x1="3" y1="22" x2="21" y2="22"/><line x1="5" y1="22" x2="5" y2="10"/><line x1="19" y1="22" x2="19" y2="10"/><path d="M5 10a7 7 0 0 1 14 0"/>',
  interconnector:
    '<path d="M4 9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2Z"/><path d="M14 21a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2Z"/><path d="M8 9v2a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9M14 15v-2"/>',
  conflict:
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
}

export function parseLocation(
  text: string,
): { lat: number; lng: number; zoom: number; name: string } | null {
  const lower = text.toLowerCase()
  const sortedKeys = Object.keys(LOCATION_COORDS).sort(
    (a, b) => b.length - a.length,
  )
  for (const key of sortedKeys) {
    if (lower.includes(key)) return { ...LOCATION_COORDS[key], name: key }
  }
  return null
}

export function parseVariable(text: string): string | null {
  const lower = text.toLowerCase()
  const sortedKeys = Object.keys(VARIABLE_ALIASES).sort(
    (a, b) => b.length - a.length,
  )
  for (const key of sortedKeys) {
    if (lower.includes(key)) return VARIABLE_ALIASES[key]
  }
  for (const overlayKey of Object.keys(overlayConfigs)) {
    if (lower.includes(overlayKey.toLowerCase())) return overlayKey
  }
  return null
}

export function parsePOIs(text: string): string[] {
  const lower = text.toLowerCase()
  const matched = new Set<string>()
  const sortedKeys = Object.keys(POI_KEYWORD_MAP).sort(
    (a, b) => b.length - a.length,
  )
  for (const key of sortedKeys) {
    if (lower.includes(key)) {
      for (const catId of POI_KEYWORD_MAP[key]) matched.add(catId)
    }
  }
  return Array.from(matched)
}

export function parseWeatherLayers(text: string): string[] {
  const lower = text.toLowerCase()
  const matched = new Set<string>()
  const sortedKeys = Object.keys(LAYER_KEYWORD_MAP).sort(
    (a, b) => b.length - a.length,
  )
  for (const key of sortedKeys) {
    if (lower.includes(key)) {
      for (const layerId of LAYER_KEYWORD_MAP[key]) matched.add(layerId)
    }
  }
  return Array.from(matched)
}
